/**
 * Meet Our Team Section
 * GSAP animations: header scroll, card hover (lift, scale, shadow), expanded panel, stickers
 * @global gsap
 * @global ScrollTrigger
 */
/// <reference path="./global.d.ts" />
(function () {
  const SECTION_SEL = '[data-meet-our-team]';
  const TITLE_SEL = '[data-meet-our-team-title]';
  const CARD_SEL = '[data-meet-our-team-card]';
  const STICKER_SEL = '[data-meet-our-team-sticker]';
  const PLUS_SEL = '[data-meet-our-team-plus]';
  const PANELS_SEL = '[data-meet-our-team-panels]';
  const PANEL_SEL = '[data-meet-our-team-panel]';
  const BACKDROP_SEL = '[data-meet-our-team-backdrop]';
  const PANEL_CLOSE_SEL = '[data-meet-our-team-panel-close]';

  /**
   * Lightweight ScrambleText-style effect for a single word.
   * @param {HTMLElement} el
   * @param {number} duration
   * @param {number} [delay]
   */
  function scrambleWord(el, duration, delay = 0) {
    if (typeof gsap === 'undefined' || !el) return;

    const chars = '!<>-_\\/[]{}—=+*^?#________';
    const finalText = el.textContent || '';
    const length = finalText.length;
    const randomChar = () => chars[Math.floor(Math.random() * chars.length)] || '';

    const state = { progress: 0 };

    gsap.to(state, {
      progress: 1,
      duration,
      delay,
      ease: 'power2.out',
      onUpdate: () => {
        const p = state.progress;
        let out = '';
        for (let i = 0; i < length; i += 1) {
          if (p < i / length) {
            out += randomChar();
          } else {
            out += finalText[i] || '';
          }
        }
        el.textContent = out;
      },
      onComplete: () => {
        el.textContent = finalText;
      },
    });
  }

  /** @param {() => void} cb */
  function waitForGsap(cb) {
    if (typeof gsap !== 'undefined') {
      if (typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
      }
      cb();
    } else if (window.matchMedia('(max-width: 989px)').matches) {
      // theme.liquid skips GSAP on the mobile homepage, and the mobile paths don't need it.
      cb();
    } else {
      // Not polled per frame: theme.liquid never loads GSAP on some mobile templates.
      document.addEventListener('gsap:ready', () => waitForGsap(cb), { once: true });
    }
  }

  /** @param {HTMLElement} section */
  function initPanel(section) {
    const panelsContainer = section.querySelector(PANELS_SEL);
    const backdrop = section.querySelector(BACKDROP_SEL);
    const closeBtns = section.querySelectorAll(PANEL_CLOSE_SEL);
    const panels = section.querySelectorAll(PANEL_SEL);

    if (!panelsContainer || !backdrop || !panels.length) return;

    const backdropEl = backdrop;

    /** @type {HTMLElement | null} */
    let activePanel = null;
    let isAnimating = false;

    /** @param {HTMLElement} card */
    function openPanel(card) {
      if (isAnimating) return;
      let blockId = card.dataset.blockId || '';
      if (!blockId) {
        const slide = card.closest('slideshow-slide');
        if (slide) {
          const slideId = slide.getAttribute('slide-id') || slide.getAttribute('slideId') || '';
          if (slideId.startsWith('team-')) blockId = slideId.slice(5);
        }
      }
      let panel = blockId ? section.querySelector(`${PANEL_SEL}[data-block-id="${blockId}"]`) : null;
      if (!panel) {
        const cardName = (card.dataset.memberName || '').trim();
        if (cardName) {
          panel = Array.from(panels).find((p) => {
            const nameEl = p.querySelector('.meet-our-team__panel-name');
            return nameEl && nameEl.textContent.trim() === cardName;
          }) || null;
        }
      }
      if (!panel) return;

      // Close any already-open panel instantly before opening the new one
      if (activePanel) {
        activePanel.setAttribute('aria-hidden', 'true');
        if (typeof gsap !== 'undefined') gsap.set(activePanel, { x: '100%' });
      }

      isAnimating = true;
      panels.forEach((p) => p.setAttribute('aria-hidden', 'true'));
      panel.setAttribute('aria-hidden', 'false');
      activePanel = /** @type {HTMLElement} */ (panel);
      backdropEl.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (typeof gsap !== 'undefined') {
        gsap.set(panel, { x: '100%' });
        gsap.set(backdropEl, { opacity: 0 });
        gsap.to(backdropEl, { opacity: 1, duration: 0.4, ease: 'power2.out' });
        gsap.to(panel, { x: 0, duration: 0.6, ease: 'power4.out', onComplete: () => { isAnimating = false; } });
        const stickerEls = panel.querySelectorAll('.meet-our-team__panel-sticker');
        if (stickerEls.length) {
          gsap.set(stickerEls, { scale: 0 });
          gsap.to(stickerEls, {
            scale: 1,
            duration: 0.5,
            stagger: 0.1,
            ease: 'back.out(1.4)',
            delay: 0.2,
          });
        }
      } else {
        isAnimating = false;
      }
    }

    function closePanel() {
      if (!activePanel || isAnimating) return;
      const panel = activePanel;
      activePanel = null;
      isAnimating = true;
      if (typeof gsap !== 'undefined') {
        gsap.to(panel, {
          x: '100%',
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            panel.setAttribute('aria-hidden', 'true');
            backdropEl.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
            isAnimating = false;
          },
        });
        gsap.to(backdropEl, { opacity: 0, duration: 0.3 });
      } else {
        panel.setAttribute('aria-hidden', 'true');
        backdropEl.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        isAnimating = false;
      }
    }

    section.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target?.closest(PLUS_SEL)) return;
      e.stopPropagation();
      e.preventDefault();
      const card = target.closest(CARD_SEL);
      if (card) openPanel(/** @type {HTMLElement} */ (card));
    });

    closeBtns.forEach((btn) => btn.addEventListener('click', closePanel));
    backdropEl.addEventListener('click', closePanel);

    section.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && activePanel) closePanel();
    });
  }

  /** @param {HTMLElement | null} section */
  function initSection(section) {
    if (!section || section.dataset.motInit === '1') return;
    section.dataset.motInit = '1';

    // Randomize slide order on page load so team members appear in a different order each time
    const slidesContainer = section.querySelector('slideshow-slides');
    if (slidesContainer) {
      const slides = Array.from(
        slidesContainer.querySelectorAll('.meet-our-team__slide')
      );
      if (slides.length > 1) {
        for (let i = slides.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          const tmp = slides[i];
          slides[i] = slides[j];
          slides[j] = tmp;
        }
        slides.forEach((slide) => slidesContainer.appendChild(slide));

        // After shuffling, make sure the carousel starts at the first slide
        const slideshow = section.querySelector('slideshow-component');
        if (typeof slidesContainer.scrollTo === 'function') {
          slidesContainer.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          slidesContainer.scrollLeft = 0;
        }
        if (slideshow && typeof slideshow.select === 'function') {
          try {
            slideshow.select(0, undefined, { animate: false });
          } catch (e) {
            // Fail silently if slideshow API is not ready
          }
        }
      }
    }

    const title = section.querySelector(TITLE_SEL);
    const cards = section.querySelectorAll(CARD_SEL);
    const progress = section.querySelector('[data-meet-our-team-progress]');
    const progressFill = section.querySelector('[data-meet-our-team-progress-fill]');

    initPanel(section);

    // Mobile: skip scroll-triggered entrance animations + hover transitions (hover doesn't fire on touch anyway).
    // Cards/title stay in their default visible state.
    const isMobile = window.matchMedia('(max-width: 989px)').matches;

    if (!isMobile && typeof gsap !== 'undefined') {
      // Header: words scramble in, then subtly split away from each other
      if (title) {
        const words = gsap.utils.toArray(
          title.querySelectorAll('.meet-our-team__title-word')
        );

        if (words.length) {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: title,
              start: 'top 85%',
              toggleActions: 'play none none none',
            },
          });

          // Rise + fade in
          tl.from(words, {
            opacity: 0,
            y: 40,
            duration: 0.9,
            stagger: 0.15,
            ease: 'power3.out',
            onStart: () => {
              // Kick off a ScrambleText-style reveal for each word
              words.forEach((word, index) => {
                scrambleWord(/** @type {HTMLElement} */ (word), 0.6, index * 0.1);
              });
            },
          });

          // Then gently split horizontally from a shared center
          tl.to(
            words,
            {
              x: (index) => {
                const center = (words.length - 1) / 2;
                return (index - center) * 16;
              },
              duration: 0.6,
              ease: 'power2.out',
            },
            '-=0.3'
          );
        }
      }

      // Cards: staggered entrance animation when the section scrolls into view
      if (cards.length) {
        gsap.fromTo(
          cards,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          }
        );
      }

      // Card hover: lift y -12px, scale 1.02, image scale 1.05, shadow, plus rotate
      cards.forEach((/** @type {HTMLElement} */ card) => {
        const sticker = card.querySelector(STICKER_SEL);
        const image = card.querySelector('.meet-our-team__card-image');
        const plusBtn = card.querySelector(PLUS_SEL);

        card.addEventListener('mouseenter', () => {
          gsap.to(card, {
            y: -12,
            scale: 1.02,
            duration: 0.4,
            ease: 'power2.out',
          });
          if (image) {
            gsap.to(image, {
              scale: 1.05,
              duration: 0.4,
              ease: 'power2.out',
              transformOrigin: 'center bottom',
            });
          }
          if (sticker) {
            gsap.to(sticker, {
              opacity: 1,
              scale: 1,
              duration: 0.3,
              ease: 'back.out(1.4)',
            });
          }
          if (plusBtn) {
            gsap.to(plusBtn.querySelector('svg'), {
              rotation: 90,
              duration: 0.4,
              ease: 'power2.out',
            });
          }
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(card, {
            y: 0,
            scale: 1,
            duration: 0.4,
            ease: 'power2.out',
          });
          if (image) {
            gsap.to(image, {
              scale: 1,
              duration: 0.4,
              ease: 'power2.out',
            });
          }
          if (sticker) {
            gsap.to(sticker, {
              opacity: 0,
              scale: 0.8,
              duration: 0.25,
              ease: 'power2.in',
            });
          }
          if (plusBtn) {
            gsap.to(plusBtn.querySelector('svg'), {
              rotation: 0,
              duration: 0.3,
              ease: 'power2.out',
            });
          }
        });
      });

      // Navigation arrows: scale feedback on click
      const arrowBtns = section.querySelectorAll('.slideshow-control');
      arrowBtns.forEach((btn) => {
        btn.addEventListener('mousedown', () => {
          gsap.to(btn, { scale: 0.95, duration: 0.1 });
        });
        btn.addEventListener('mouseup', () => {
          gsap.to(btn, { scale: 1, duration: 0.2, ease: 'power2.out' });
        });
        btn.addEventListener('mouseleave', () => {
          gsap.to(btn, { scale: 1, duration: 0.2 });
        });
      });
    }

    // Carousel progress bar (mobile)
    if (progress && progressFill) {
      const scroller = section.querySelector('slideshow-slides');
      if (scroller) {
        const slides = scroller.querySelectorAll('.meet-our-team__slide');
        const total = slides.length;
        if (total > 0) {
          const updateProgress = () => {
            const rect = scroller.getBoundingClientRect();
            const scrollLeft = scroller.scrollLeft;
            const scrollWidth = scroller.scrollWidth - rect.width;
            const pct = scrollWidth > 0 ? (scrollLeft / scrollWidth) * 100 : 0;
            const isMobile = window.matchMedia
              ? window.matchMedia('(max-width: 749px)').matches
              : window.innerWidth < 750;

            if (isMobile) {
              // On mobile, fill the bar from 0% → 100% with the red accent
              progressFill.style.width = `${pct}%`;
              progressFill.style.marginLeft = '0';
            } else {
              // On larger screens, keep the pill-style indicator that moves along the track
              const fillWidth = 100 / total;
              const offset = (pct / 100) * (100 - fillWidth);
              progressFill.style.width = `${fillWidth}%`;
              progressFill.style.marginLeft = `${offset}%`;
            }
          };
          scroller.addEventListener('scroll', updateProgress);
          updateProgress();
        }
      }
    }

  }

  function init() {
    const sections = document.querySelectorAll(SECTION_SEL);
    sections.forEach(initSection);
    if (sections.length && typeof ScrollTrigger !== 'undefined') {
      let resizeT;
      window.addEventListener('resize', () => {
        clearTimeout(resizeT);
        resizeT = setTimeout(ScrollTrigger.refresh, 250);
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    waitForGsap(init);
  });

  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (target instanceof Document) {
      target.querySelectorAll(SECTION_SEL).forEach(initSection);
    } else if (target instanceof HTMLElement) {
      const section = target.closest(SECTION_SEL) || target.querySelector(SECTION_SEL);
      if (section) initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const target = e.target;
    if (target instanceof HTMLElement) {
      const section = target.closest(SECTION_SEL);
      if (section) section.removeAttribute('data-mot-init');
    }
  });
})();
